import React, {Component} from 'react';
import {connect} from 'react-redux';
import {withTranslation} from 'react-i18next';
import {
  Button,
  Form,
  Message,
  Modal
} from 'semantic-ui-react';
import {toast} from 'react-toastify';
import BlockUserButton from '../../components/user/blockUserButton';
import {useFeatureFlags} from '../../hooks/useFeatureFlags';

const userIdOf = user => (typeof user === 'string' ? user : user?._id);

export class ReportOrBlockControl extends Component {
  constructor(props) {
    super(props);
    const firstMessage = this.reportableMessages(props)[0];
    this.state = {
      open: false,
      messageId: firstMessage && firstMessage._id,
      reason: 'scam',
      comment: '',
      submitting: false,
      error: undefined
    };
  }

  reportableMessages = props => {
    const {currentConversation, userProfile} = props;
    return (currentConversation?.messages || []).filter(
      message => userIdOf(message.from) !== userProfile?._id
    );
  };

  selectedMessage = () =>
    this.reportableMessages(this.props).find(
      message => message._id === this.state.messageId
    );

  submitReport = async () => {
    const {currentConversation, idToken, t} = this.props;
    const {messageId, reason, comment} = this.state;
    this.setState({submitting: true, error: undefined});
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_HOST}${process.env.REACT_APP_API_PATH}/conversations/${currentConversation._id}/reports`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'br-token': idToken
          },
          body: JSON.stringify({messageId, reason, comment})
        }
      );
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.msg || t('FailedToSubmitReport'));
      }
      toast.info(t('ReportSubmitted'));
      this.setState({open: false, submitting: false});
    } catch (error) {
      this.setState({submitting: false, error: error.message});
    }
  };

  render() {
    const {featureEnabled, currentConversation, t} = this.props;
    const reportableMessages = this.reportableMessages(this.props);
    if (
      !featureEnabled ||
      !currentConversation?._id ||
      reportableMessages.length === 0
    ) {
      return null;
    }
    const selectedMessage = this.selectedMessage() || reportableMessages[0];
    const reportedUserId = userIdOf(selectedMessage.from);
    const reportedUserName =
      selectedMessage.from?.displayName || t('ConversationMember');
    const {open, messageId, reason, comment, submitting, error} = this.state;

    return (
      <React.Fragment>
        <Button
          type='button'
          basic
          size='tiny'
          icon='flag outline'
          content={t('ReportOrBlock')}
          aria-label={t('ReportOrBlock')}
          onClick={() => this.setState({open: true})}
        />
        <Modal
          size='small'
          open={open}
          onClose={() => this.setState({open: false, error: undefined})}
        >
          <Modal.Header>{t('ReportOrBlock')}</Modal.Header>
          <Modal.Content>
            <p>{t('ReportPrivacyNotice')}</p>
            {error && <Message error content={error} />}
            <Form>
              <Form.Select
                label={t('MessageToReport')}
                value={messageId}
                options={reportableMessages.map((message, index) => ({
                  key: message._id,
                  value: message._id,
                  text: `${t('MessageFrom')} ${
                    message.from?.displayName || t('ConversationMember')
                  } #${reportableMessages.length - index}`
                }))}
                onChange={(event, data) =>
                  this.setState({messageId: data.value})
                }
              />
              <Form.Select
                label={t('Reason')}
                value={reason}
                options={['scam', 'spam', 'harassment', 'other'].map(value => ({
                  key: value,
                  value,
                  text: t(`ReportReason_${value}`)
                }))}
                onChange={(event, data) => this.setState({reason: data.value})}
              />
              <Form.TextArea
                label={t('OptionalDetails')}
                maxLength={1000}
                value={comment}
                onChange={(event, data) => this.setState({comment: data.value})}
              />
            </Form>
            <div style={{marginTop: '1em'}}>
              <BlockUserButton
                userId={reportedUserId}
                displayName={reportedUserName}
                basic
              />
            </div>
          </Modal.Content>
          <Modal.Actions>
            <Button
              type='button'
              onClick={() => this.setState({open: false})}
              content={t('Cancel')}
            />
            <Button
              type='button'
              negative
              loading={submitting}
              disabled={!messageId || submitting}
              onClick={this.submitReport}
              content={t('SubmitReport')}
            />
          </Modal.Actions>
        </Modal>
      </React.Fragment>
    );
  }
}

const FeatureAwareReportOrBlock = props => {
  const {flags} = useFeatureFlags();
  const featureEnabled = !!(
    flags?.moderation_reporting ||
    (flags?.moderation_reporting_beta_access_allowed &&
      props.userProfile?.betaFeatures?.moderation_reporting)
  );
  return (
    <ReportOrBlockControl {...props} featureEnabled={featureEnabled} />
  );
};

export default withTranslation()(
  connect(state => ({
    idToken: state.user.idToken,
    userProfile: state.user.userProfile,
    currentConversation: state.message.currentConversation
  }))(FeatureAwareReportOrBlock)
);
